import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Récupère une image distante et retourne un objet { dataUrl, format, width, height }
 * compatible avec jsPDF.addImage. Retourne null si le fetch échoue (CORS, 404, …).
 */
async function fetchImageAsDataURL(url) {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    // Déduire le format : PNG / JPEG / WEBP (jsPDF supporte PNG/JPEG/WEBP)
    let format = "PNG";
    if (/^data:image\/jpe?g/i.test(dataUrl)) format = "JPEG";
    else if (/^data:image\/webp/i.test(dataUrl)) format = "WEBP";
    // Mesurer les dimensions
    const { width, height } = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = dataUrl;
    });
    return { dataUrl, format, width, height };
  } catch {
    return null;
  }
}

/**
 * Génère une facture PDF pour un client donné (async car peut charger un logo distant).
 *
 * @param {Object} params
 * @param {Object} params.appConfig - Config du tenant (billing_*, logo_url)
 * @param {Object} params.client    - { client_id, client_name, logements: [...] }
 * @param {number} params.year
 * @param {number} params.month     - 1-12
 * @param {string} [params.invoiceNumber] - Numéro facture (auto-généré si absent)
 */
export async function generateClientInvoicePDF({ appConfig, client, year, month, invoiceNumber }) {
  const doc = new jsPDF();
  const marginX = 14;
  let y = 15;

  const cfg = appConfig || {};
  const companyName = cfg.billing_company_name || cfg.company_name || "Votre société";
  const legalForm = cfg.billing_legal_form || "";
  const address = cfg.billing_address || "";
  const zip = cfg.billing_zip || "";
  const city = cfg.billing_city || "";
  const country = cfg.billing_country || "";
  const siret = cfg.billing_siret || "";
  const vat = cfg.billing_vat_number || "";
  const email = cfg.billing_email || "";
  const phone = cfg.billing_phone || "";
  const iban = cfg.billing_iban || "";
  const bic = cfg.billing_bic || "";
  const footer = cfg.billing_footer || "";

  const monthNames = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
  ];
  const periodLabel = `${monthNames[(month || 1) - 1]} ${year}`;
  const dateStr = new Date().toLocaleDateString("fr-FR");
  const invNum = invoiceNumber ||
    `INV-${year}${String(month).padStart(2, "0")}-${(client.client_id || client.client_name || "X")
      .toString()
      .slice(0, 6)
      .toUpperCase()}`;

  // ==== EN-TÊTE ÉMETTEUR (haut gauche) ====
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(companyName, marginX, y);
  y += 5;
  if (legalForm) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(legalForm, marginX, y);
    y += 4;
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  if (address) { doc.text(address, marginX, y); y += 4; }
  const cityLine = [zip, city].filter(Boolean).join(" ");
  if (cityLine) { doc.text(cityLine, marginX, y); y += 4; }
  if (country) { doc.text(country, marginX, y); y += 4; }
  if (email) { doc.text(`Email : ${email}`, marginX, y); y += 4; }
  if (phone) { doc.text(`Tél : ${phone}`, marginX, y); y += 4; }
  if (siret) { doc.text(`SIRET : ${siret}`, marginX, y); y += 4; }
  if (vat) { doc.text(`TVA : ${vat}`, marginX, y); y += 4; }

  // ==== NUMÉRO / DATE (haut droite) ====
  // Charger le logo si disponible (haut-droite au-dessus du bloc N° facture)
  let numeroYOffset = 0;
  if (cfg.logo_url) {
    const logo = await fetchImageAsDataURL(cfg.logo_url);
    if (logo && logo.width > 0) {
      // Logo cible: 30mm de large max, hauteur proportionnelle
      const maxW = 30;
      const maxH = 20;
      const ratio = Math.min(maxW / (logo.width / 4), maxH / (logo.height / 4));
      const w = (logo.width / 4) * ratio;
      const h = (logo.height / 4) * ratio;
      try {
        doc.addImage(logo.dataUrl, logo.format, 200 - marginX - w, 12, w, h);
        numeroYOffset = h + 4;
      } catch { /* si l'image est invalide on ignore le logo */ }
    }
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("FACTURE", 200 - marginX, 20 + numeroYOffset, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`N° ${invNum}`, 200 - marginX, 27 + numeroYOffset, { align: "right" });
  doc.text(`Émise le ${dateStr}`, 200 - marginX, 32 + numeroYOffset, { align: "right" });
  doc.text(`Période : ${periodLabel}`, 200 - marginX, 37 + numeroYOffset, { align: "right" });

  // ==== FACTURÉ À ====
  y = Math.max(y + 4, 60 + numeroYOffset);
  doc.setDrawColor(200);
  doc.line(marginX, y, 200 - marginX, y);
  y += 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Facturé à :", marginX, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(client.client_name || "Client", marginX, y);
  y += 6;

  // ==== LIGNES ====
  const logements = client.logements || [];
  const rows = [];

  // Format court une date ISO "YYYY-MM-DD" → "02 oct."
  const monthShort = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  const fmtDate = (iso) => {
    if (!iso) return "";
    const parts = String(iso).slice(0, 10).split("-");
    if (parts.length !== 3) return iso;
    const d = parseInt(parts[2], 10);
    const m = parseInt(parts[1], 10) - 1;
    if (isNaN(d) || isNaN(m) || m < 0 || m > 11) return iso;
    return `${String(d).padStart(2, "0")} ${monthShort[m]}`;
  };

  logements.forEach((log) => {
    // Fix UUID : utiliser logement_name (toujours renseigné par backend) sans fallback housing_id
    const lot = log.logement_name || log.pseudo || log.housing_name || "Logement";

    if ((log.nb_menages || 0) > 0) {
      rows.push([
        `Ménages — ${lot}`,
        log.nb_menages,
        `${Number(log.prix_menages_total || 0).toFixed(2)} €`,
      ]);
      // Sous-ligne : dates d'intervention sans l'intervenant
      const dates = Array.isArray(log.dates_interventions) ? log.dates_interventions : [];
      if (dates.length > 0) {
        const dateStrings = dates
          .map((d) => fmtDate(typeof d === "string" ? d : d.date))
          .filter(Boolean);
        if (dateStrings.length > 0) {
          rows.push([
            { content: `Dates : ${dateStrings.join(", ")}`, styles: { fontStyle: "italic", textColor: [90, 90, 90], fontSize: 8 } },
            "",
            "",
          ]);
        }
      }
    }

    // Parures : afficher UNIQUEMENT si le logement gère les parures ET nb_parures > 0
    const gestionParures = log.gestion_parures !== false; // default true si undefined
    if (gestionParures && (log.nb_parures_total || 0) > 0) {
      rows.push([
        `Parures — ${lot}`,
        log.nb_parures_total,
        `${Number(log.tarif_parures_total || 0).toFixed(2)} €`,
      ]);
    }

    if ((log.total_consommables || 0) > 0) {
      rows.push([
        `Consommables — ${lot}`,
        "",
        `${Number(log.total_consommables || 0).toFixed(2)} €`,
      ]);
    }
    if ((log.total_prestations || 0) > 0) {
      rows.push([
        `Prestations diverses — ${lot}`,
        "",
        `${Number(log.total_prestations || 0).toFixed(2)} €`,
      ]);
    }
  });

  const total = logements.reduce((s, l) => s + Number(l.total_logement || 0), 0);

  autoTable(doc, {
    startY: y + 2,
    head: [["Désignation", "Qté", "Montant HT"]],
    body: rows.length ? rows : [["Aucune prestation sur la période", "", "0.00 €"]],
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [51, 65, 85], textColor: [255, 255, 255] },
    columnStyles: {
      1: { halign: "center", cellWidth: 22 },
      2: { halign: "right", cellWidth: 34 },
    },
    margin: { left: marginX, right: marginX },
  });

  // ==== TOTAUX ====
  const afterTable = doc.lastAutoTable.finalY + 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Total :", 150, afterTable, { align: "right" });
  doc.text(`${total.toFixed(2)} €`, 200 - marginX, afterTable, { align: "right" });

  const tvaLine = vat
    ? `TVA (${vat}) : incluse le cas échéant`
    : "TVA non applicable, art. 293 B du CGI";
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(tvaLine, 200 - marginX, afterTable + 5, { align: "right" });

  // ==== COORDONNÉES BANCAIRES ====
  let footerY = afterTable + 14;
  if (iban) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Règlement par virement :", marginX, footerY);
    footerY += 4;
    doc.setFont("helvetica", "normal");
    doc.text(`IBAN : ${iban}`, marginX, footerY);
    footerY += 4;
    if (bic) { doc.text(`BIC : ${bic}`, marginX, footerY); footerY += 4; }
  }

  // ==== MENTIONS LÉGALES / PIED DE PAGE ====
  if (footer) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7);
    const wrapped = doc.splitTextToSize(footer, 180);
    doc.text(wrapped, marginX, Math.max(footerY + 6, 275));
  }

  // ==== TÉLÉCHARGER ====
  const safeName = (client.client_name || "client").replace(/[^a-z0-9]+/gi, "_");
  doc.save(`facture_${year}${String(month).padStart(2, "0")}_${safeName}.pdf`);
}
